import type { Citation } from "@/lib/citations";

/**
 * The primary sources behind the free RIDDOR checker
 * (/free/riddor-reportable-checker) and the "RIDDOR if you are self-employed"
 * section of /guides/incident-reporting-riddor. Every line the checker shows
 * cites one of these, read on the accessed date:
 *
 *  - RIDDOR 2013 (SI 2013/1471) on legislation.gov.uk, regs 2 to 9, 12 and 14,
 *    Schedule 1 (reporting procedure and records) and Schedule 2 (dangerous
 *    occurrences)
 *  - the Social Security (Claims and Payments) Regulations 1979, reg 25 (the
 *    accident book for employers of 10 or more)
 *  - the Health and Safety at Work etc. Act 1974 (General Duties of
 *    Self-Employed Persons) (Prescribed Undertakings) Regulations 2015, the
 *    Schedule (construction work is a prescribed undertaking, so a self-employed
 *    builder is a "relevant self-employed person" for diseases, RIDDOR reg 3(3))
 *  - HSE's RIDDOR pages (who should report, how to report, reportable
 *    incidents) and HSENI for Northern Ireland
 */
const ACCESSED = "2026-10-31";

const RIDDOR = "https://www.legislation.gov.uk/uksi/2013/1471";

/** legislation.gov.uk URL of one RIDDOR 2013 regulation. */
export function riddorRegulationUrl(regulation: number): string {
  return `${RIDDOR}/regulation/${regulation}`;
}

/** legislation.gov.uk URL of one RIDDOR 2013 schedule. */
export function riddorScheduleUrl(schedule: 1 | 2): string {
  return `${RIDDOR}/schedule/${schedule}`;
}

export const SS_CLAIMS_REG_25_URL = "https://www.legislation.gov.uk/uksi/1979/628/regulation/25";
export const PRESCRIBED_UNDERTAKINGS_URL = "https://www.legislation.gov.uk/uksi/2015/1583/schedule";
export const RIDDOR_NI_URL = "https://www.legislation.gov.uk/nisr/1997/455/contents/made";
export const HSENI_ACCIDENT_REPORTING_URL = "https://www.hseni.gov.uk/articles/accident-reporting";
export const HSE_WHO_SHOULD_REPORT_URL = "https://www.hse.gov.uk/riddor/reporting/who-should-report.htm";
export const HSE_HOW_TO_REPORT_URL = "https://www.hse.gov.uk/riddor/reporting/how-to-make-riddor-report.htm";
export const HSE_REPORTABLE_INCIDENTS_URL = "https://www.hse.gov.uk/riddor/types-of-reportable-incidents.htm";
export const HSE_DANGEROUS_OCCURRENCES_URL = "https://www.hse.gov.uk/riddor/dangerous-occurences.htm";
export const HSE_OCCUPATIONAL_DISEASES_URL = "https://www.hse.gov.uk/riddor/occupational-diseases.htm";
export const HSE_WORK_RELATED_DEATHS_URL = "https://www.hse.gov.uk/enforce/wrdp/index.htm";

const reg = (regulation: number, title: string): Citation => ({
  title: `The Reporting of Injuries, Diseases and Dangerous Occurrences Regulations 2013, regulation ${regulation} (${title})`,
  url: riddorRegulationUrl(regulation),
  publisher: "legislation.gov.uk",
  accessedAt: ACCESSED,
});

const hse = (title: string, url: string): Citation => ({ title, url, publisher: "Health and Safety Executive", accessedAt: ACCESSED });

export const RIDDOR_CHECKER_SOURCES: readonly Citation[] = [
  reg(2, "interpretation"),
  reg(3, "responsible person"),
  reg(4, "non-fatal injuries to workers"),
  reg(5, "non-fatal injuries to non-workers"),
  reg(6, "work-related fatalities"),
  reg(7, "dangerous occurrences"),
  reg(8, "occupational diseases"),
  reg(9, "exposure to carcinogens, mutagens and biological agents"),
  reg(12, "recording and record-keeping"),
  reg(14, "restrictions on the application of regulations 4 to 10"),
  {
    title: "The Reporting of Injuries, Diseases and Dangerous Occurrences Regulations 2013, Schedule 1 (reporting and recording procedures)",
    url: riddorScheduleUrl(1),
    publisher: "legislation.gov.uk",
    accessedAt: ACCESSED,
  },
  {
    title: "The Reporting of Injuries, Diseases and Dangerous Occurrences Regulations 2013, Schedule 2 (dangerous occurrences)",
    url: riddorScheduleUrl(2),
    publisher: "legislation.gov.uk",
    accessedAt: ACCESSED,
  },
  {
    title: "The Social Security (Claims and Payments) Regulations 1979, regulation 25 (obligations of employers)",
    url: SS_CLAIMS_REG_25_URL,
    publisher: "legislation.gov.uk",
    accessedAt: ACCESSED,
  },
  {
    title:
      "The Health and Safety at Work etc. Act 1974 (General Duties of Self-Employed Persons) (Prescribed Undertakings) Regulations 2015, Schedule",
    url: PRESCRIBED_UNDERTAKINGS_URL,
    publisher: "legislation.gov.uk",
    accessedAt: ACCESSED,
  },
  {
    title: "The Reporting of Injuries, Diseases and Dangerous Occurrences Regulations (Northern Ireland) 1997",
    url: RIDDOR_NI_URL,
    publisher: "legislation.gov.uk",
    accessedAt: ACCESSED,
  },
  hse("Make a RIDDOR report: who should report under RIDDOR", HSE_WHO_SHOULD_REPORT_URL),
  hse("Make a RIDDOR report: how to make a RIDDOR report", HSE_HOW_TO_REPORT_URL),
  hse("Types of reportable incidents", HSE_REPORTABLE_INCIDENTS_URL),
  hse("Dangerous occurrences", HSE_DANGEROUS_OCCURRENCES_URL),
  hse("Occupational diseases", HSE_OCCUPATIONAL_DISEASES_URL),
  hse("Work-related deaths protocol", HSE_WORK_RELATED_DEATHS_URL),
  {
    title: "Accident reporting (RIDDOR) in Northern Ireland",
    url: HSENI_ACCIDENT_REPORTING_URL,
    publisher: "Health and Safety Executive for Northern Ireland",
    accessedAt: ACCESSED,
  },
];
